import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import db from '../../db/db';
import { setAlert } from '../../actions/alert';

const Revision = () => {
    const dispatch = useDispatch();

    const [empleadoLogueado, setEmpleadoLogueado] = useState(null);
    const [revisionActiva, setRevisionActiva]     = useState(null);
    const [eventoActivo, setEventoActivo]         = useState(null);
    const [corteActivo, setCorteActivo]           = useState(null);
    const [fecha] = useState(new Date());
    const [puntos, setPuntos]                     = useState([]);
    const [observaciones, setObservaciones]       = useState('');
    const [statusGuardar, setStatusGuardar]       = useState(false);
    const [cargando, setCargando]                 = useState(true);

    useEffect(() => {
        const init = async () => {
            // Paso 1 — Sesión
            const sesion   = await db.sesion.toArray();
            const logueado = sesion[0];
            setEmpleadoLogueado(logueado);

            // Paso 2 — Obtener puntos de la sucursal
            const sucursalId = logueado.sucursal?._id || logueado.sucursal;
            const sucursal   = await db.sucursales
                .filter(s => String(s._id || s.id) === String(sucursalId))
                .first();
            setEventoActivo(sucursal?.evento || null);

            const puntosBase = (sucursal?.puntos || []).map(p => ({
                nombre: p.nombre,
                status: false,
            }));

            // Paso 3 — Verificar corte activo
            const corte = await db.cortes
                .filter(c => c.status === false && String(c.sucursal?._id || c.sucursal) === String(sucursalId))
                .first();

            if (!corte) {
                setCargando(false);
                return;
            }

            setCorteActivo(corte);

            // Paso 4 — Detectar revisión vinculada al corte activo
            const revisionExistente = await db.revisiones
                .filter(r => r.corte_local_id === corte.id
                    && String(r.sucursal?._id || r.sucursal) === String(sucursalId))
                .first();

            if (revisionExistente) {
                setRevisionActiva(revisionExistente);
                setObservaciones(revisionExistente.observaciones_creacion || '');

                // Combinar puntos guardados con puntos actuales de la sucursal
                const puntosGuardados = revisionExistente.puntos || [];
                const merged = puntosBase.map(p => {
                    const guardado = puntosGuardados.find(g => g.nombre === p.nombre);
                    return { nombre: p.nombre, status: guardado ? guardado.status : false };
                });
                // Incluir puntos guardados que ya no estén en la sucursal
                puntosGuardados.forEach(g => {
                    if (!merged.find(m => m.nombre === g.nombre)) {
                        merged.push({ nombre: g.nombre, status: g.status });
                    }
                });
                setPuntos(merged);
            } else {
                setPuntos(puntosBase);
            }
        };

        init().finally(() => setCargando(false));
    }, []);

    const handleToggle = (nombre) => {
        setPuntos(prev =>
            prev.map(p => p.nombre === nombre ? { ...p, status: !p.status } : p)
        );
    };

    const handleGuardar = async () => {
        setStatusGuardar(true);

        const registro = {
            sucursal:               empleadoLogueado.sucursal?._id || empleadoLogueado.sucursal,
            responsable:            empleadoLogueado._id,
            evento:                 eventoActivo?._id || null,
            corte_local_id:         corteActivo?.id || null,
            puntos:                 puntos.map(p => ({ nombre: p.nombre, status: p.status })),
            observaciones_creacion: observaciones,
            fecha_creacion:         new Date(),
        };

        try {
            if (revisionActiva) {
                await db.revisiones.update(revisionActiva.id, registro);
            } else {
                const nuevoId = await db.revisiones.add(registro);
                setRevisionActiva({ ...registro, id: nuevoId });
            }
            dispatch(setAlert('Revisión guardada correctamente', 'success'));
        } catch {
            dispatch(setAlert('Error al guardar la revisión', 'danger'));
        } finally {
            setStatusGuardar(false);
        }
    };

    const fechaLarga = fecha.toLocaleDateString('es-MX', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

    const completados = puntos.filter(p => p.status).length;

    if (cargando) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 120px)' }}>
            <span className="spinner-border" style={{ color: '#F5C800' }} role="status" />
        </div>
    );

    if (!corteActivo) return (
        <div style={styles.page}>
            <div style={styles.encabezado}>
                <div>
                    <div style={styles.encabezadoTitulo}>
                        <i className="fas fa-tasks me-2" style={{ color: '#F5C800' }}></i>
                        Revisión
                    </div>
                </div>
            </div>
            <div style={styles.bloqueado}>
                <i className="fas fa-lock" style={{ fontSize: '2.5rem', color: '#444', marginBottom: '1rem' }}></i>
                <p style={{ color: '#888', margin: 0, fontSize: '0.95rem', textAlign: 'center', lineHeight: 1.6 }}>
                    No hay un corte de caja activo.<br />
                    Inicia un corte antes de realizar la revisión.
                </p>
            </div>
        </div>
    );

    return (
        <div style={styles.page}>
            {/* Encabezado */}
            <div style={styles.encabezado}>
                <div>
                    <div style={styles.encabezadoTitulo}>
                        <i className="fas fa-tasks me-2" style={{ color: '#F5C800' }}></i>
                        Revisión
                    </div>
                    <div style={styles.fechaLabel}>
                        {fechaLarga.charAt(0).toUpperCase() + fechaLarga.slice(1)}
                    </div>
                </div>
                <div style={styles.resumenBadge}>
                    <span style={styles.resumenNum}>{completados}</span>
                    <span style={styles.resumenTotal}>/ {puntos.length}</span>
                    <span style={styles.resumenLabel}>completados</span>
                </div>
            </div>

            {/* Lista de puntos */}
            <div style={styles.card}>
                {puntos.length === 0 ? (
                    <div style={styles.vacio}>
                        <i className="fas fa-clipboard" style={{ fontSize: '2rem', color: '#444', marginBottom: '0.75rem' }}></i>
                        <p style={{ color: '#888', margin: 0 }}>Esta sucursal no tiene puntos de revisión configurados.</p>
                    </div>
                ) : (
                    puntos.map((punto, idx) => (
                        <div
                            key={punto.nombre}
                            style={{
                                ...styles.fila,
                                borderBottom: idx < puntos.length - 1 ? '1px solid #222' : 'none',
                                background: punto.status ? 'rgba(245,200,0,0.04)' : 'transparent',
                            }}
                            onClick={() => handleToggle(punto.nombre)}
                        >
                            <div style={styles.puntoInfo}>
                                <div style={{
                                    ...styles.icono,
                                    background: punto.status ? 'rgba(245,200,0,0.15)' : '#222',
                                    border: punto.status ? '2px solid rgba(245,200,0,0.4)' : '2px solid #333',
                                }}>
                                    <i
                                        className="fas fa-clipboard-check"
                                        style={{ color: punto.status ? '#F5C800' : '#555', fontSize: '1rem' }}
                                    ></i>
                                </div>
                                <span style={{ ...styles.nombrePunto, color: punto.status ? '#F0F0F0' : '#888' }}>
                                    {punto.nombre}
                                </span>
                            </div>
                            <div style={{
                                ...styles.check,
                                background: punto.status ? '#F5C800' : 'transparent',
                                border: punto.status ? '2px solid #F5C800' : '2px solid #444',
                            }}>
                                {punto.status && (
                                    <i className="fas fa-check" style={{ color: '#111', fontSize: '0.9rem' }}></i>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Observaciones */}
            <div style={styles.obsWrap}>
                <label style={styles.obsLabel}>
                    <i className="fas fa-comment-alt me-2" style={{ color: '#555' }}></i>
                    Observaciones
                </label>
                <textarea
                    style={styles.obsTextarea}
                    rows={4}
                    placeholder="Escribe aquí cualquier observación relevante de la revisión..."
                    value={observaciones}
                    onChange={e => setObservaciones(e.target.value)}
                />
            </div>

            {/* Botón guardar */}
            <button
                style={statusGuardar || puntos.length === 0
                    ? { ...styles.btnGuardar, opacity: 0.6, cursor: 'not-allowed' }
                    : styles.btnGuardar
                }
                onClick={handleGuardar}
                disabled={statusGuardar || puntos.length === 0}
            >
                {statusGuardar ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Guardando...
                    </>
                ) : (
                    <>
                        <i className="fas fa-save me-2"></i>
                        {revisionActiva ? 'Actualizar revisión' : 'Guardar revisión'}
                    </>
                )}
            </button>
        </div>
    );
};

const styles = {
    page: {
        maxWidth: '680px',
        margin: '0 auto',
    },
    encabezado: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.25rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    encabezadoTitulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1.2rem',
        marginBottom: '0.25rem',
    },
    fechaLabel: {
        color: '#888888',
        fontSize: '0.9rem',
    },
    resumenBadge: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '12px',
        padding: '0.5rem 1rem',
        textAlign: 'center',
        minWidth: '80px',
    },
    resumenNum: {
        color: '#F5C800',
        fontWeight: 800,
        fontSize: '1.4rem',
    },
    resumenTotal: {
        color: '#555',
        fontWeight: 700,
        fontSize: '1.1rem',
        marginLeft: '2px',
    },
    resumenLabel: {
        display: 'block',
        color: '#666',
        fontSize: '0.75rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
        marginBottom: '1.25rem',
    },
    vacio: {
        padding: '3rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    bloqueado: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '3rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    fila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 1.25rem',
        cursor: 'pointer',
        transition: 'background 0.15s',
        userSelect: 'none',
    },
    puntoInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.9rem',
    },
    icono: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.2s',
    },
    nombrePunto: {
        fontWeight: 600,
        fontSize: '1rem',
        transition: 'color 0.2s',
    },
    check: {
        width: '28px',
        height: '28px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.2s',
    },
    obsWrap: {
        marginBottom: '1.25rem',
    },
    obsLabel: {
        color: '#888888',
        fontSize: '0.85rem',
        fontWeight: 600,
        display: 'block',
        marginBottom: '0.5rem',
    },
    obsTextarea: {
        width: '100%',
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        color: '#F0F0F0',
        padding: '0.75rem 1rem',
        fontSize: '0.95rem',
        resize: 'vertical',
        outline: 'none',
        boxSizing: 'border-box',
    },
    btnGuardar: {
        background: '#F5C800',
        color: '#111111',
        border: 'none',
        borderRadius: '12px',
        padding: '1rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        width: '100%',
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
};

export default Revision;
